import _extends from '@babel/runtime/helpers/extends';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import classnames from 'classnames';
import React, { useState, useRef, useEffect } from 'react';
import useActiveElement from '../hooks/useActiveElement.js';
import FocusTrap from '../utils/FocusTrap.js';
import alertUserOfDeprecatedProps from '../utils/deprecation.js';
import Button from './Button.js';
import Caption from './Caption.js';
import Card from './Card.js';
import Grid from './Grid.js';
import Heading from './Heading.js';
import Image from './Image.js';
import Overlay from './Overlay.js';

var _excluded = ["className", "children", "data-testid", "hasOverlay", "spotlightLocation", "spotlightIsVisible", "spotlightAnimation", "spotlightAnimationDuration", "contentData", "contentLocation", "contentIsVisible", "size", "onCloseContent"];
var Spotlight = function Spotlight(_ref) {
  var _classnames2;
  var className = _ref.className,
    children = _ref.children,
    dataTestId = _ref['data-testid'],
    _ref$hasOverlay = _ref.hasOverlay,
    hasOverlay = _ref$hasOverlay === void 0 ? false : _ref$hasOverlay,
    _ref$spotlightLocatio = _ref.spotlightLocation,
    spotlightLocation = _ref$spotlightLocatio === void 0 ? 'top-left' : _ref$spotlightLocatio,
    _ref$spotlightIsVisib = _ref.spotlightIsVisible,
    spotlightIsVisible = _ref$spotlightIsVisib === void 0 ? true : _ref$spotlightIsVisib,
    spotlightAnimation = _ref.spotlightAnimation,
    _ref$spotlightAnimati = _ref.spotlightAnimationDuration,
    spotlightAnimationDuration = _ref$spotlightAnimati === void 0 ? 60000 : _ref$spotlightAnimati,
    _ref$contentData = _ref.contentData,
    contentData = _ref$contentData === void 0 ? [{}] : _ref$contentData,
    _ref$contentLocation = _ref.contentLocation,
    contentLocation = _ref$contentLocation === void 0 ? 'top-left' : _ref$contentLocation,
    contentIsVisible = _ref.contentIsVisible,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? 'normal' : _ref$size,
    onCloseContent = _ref.onCloseContent,
    otherProps = _objectWithoutProperties(_ref, _excluded);
  alertUserOfDeprecatedProps('Spotlight', otherProps);

  // visibility toggle for card content
  var _useState = useState(contentIsVisible),
    _useState2 = _slicedToArray(_useState, 2),
    isContentVisible = _useState2[0],
    setIsContentVisible = _useState2[1];

  // Cache the current focused dom element across re-renders
  var focusedElRef = useActiveElement(isContentVisible);
  var spotlightHeading = useRef(null);

  // animation controls for spotlight
  var _useState3 = useState(spotlightAnimation),
    _useState4 = _slicedToArray(_useState3, 2),
    isSpotlightAnimating = _useState4[0],
    setIsSpotlightAnimating = _useState4[1];
  if (otherProps['light'] || otherProps['dark']) {
    console.error('Hey Canvas User! The Spotlight component no longer supports the dark and light props. Try hasOverlay instead.');
  }

  // timeout for spotlightAnimation
  useEffect(function () {
    // only run this if we are animating, otherwise its wasted bandwidth
    if (spotlightAnimation && spotlightAnimationDuration !== 'infinite') {
      var timer = setTimeout(function () {
        setIsSpotlightAnimating(false);
      }, spotlightAnimationDuration);
      return function () {
        return clearTimeout(timer);
      };
    }
  }, [spotlightAnimation, spotlightAnimationDuration]);

  // current slide controls
  var _useState5 = useState(0),
    _useState6 = _slicedToArray(_useState5, 2),
    currentSlideIndex = _useState6[0],
    setCurrentSlideIndex = _useState6[1];
  var finalClasses = classnames('C-Spotlight', className);
  var finalSpotlightContainerClasses = classnames('C-Spotlight__Container', _defineProperty({}, "--location-".concat(spotlightLocation), spotlightLocation), className);
  var finalSpotlightClasses = classnames('C-Spotlight__Light', {
    isAnimating: isSpotlightAnimating,
    isVisible: spotlightIsVisible
  }, className);
  var finalContainerClasses = classnames('C-Spotlight__Content', (_classnames2 = {}, _defineProperty(_classnames2, "--location-".concat(contentLocation), contentLocation), _defineProperty(_classnames2, "--size-".concat(size), size), _classnames2), className);
  var handleSpotlightClick = function handleSpotlightClick() {
    setIsSpotlightAnimating(false);
    setIsContentVisible(true);
  };
  var handleContentClose = function handleContentClose() {
    setIsContentVisible(false);
    setCurrentSlideIndex(0);
    onCloseContent && onCloseContent(currentSlideIndex === contentData.length - 1);
  };
  var handleNextClick = function handleNextClick() {
    setCurrentSlideIndex(currentSlideIndex + 1);
    spotlightHeading.current.focus();
  };
  return /*#__PURE__*/React.createElement("div", _extends({
    className: finalClasses,
    "data-testid": dataTestId
  }, otherProps), /*#__PURE__*/React.createElement("div", {
    "data-testid": dataTestId ? "".concat(dataTestId, "_container") : '',
    className: finalSpotlightContainerClasses
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    "data-testid": dataTestId ? "".concat(dataTestId, "_spotlight") : '',
    role: "alert",
    "aria-label": otherProps['aria-label'] || 'Notification tooltip',
    className: finalSpotlightClasses,
    onClick: handleSpotlightClick
  }), isContentVisible && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    "data-testid": dataTestId ? "".concat(dataTestId, "_content") : '',
    className: finalContainerClasses
  }, /*#__PURE__*/React.createElement(Card, {
    elevation: "4"
  }, /*#__PURE__*/React.createElement(FocusTrap, {
    active: true,
    returnFocusTo: focusedElRef.current,
    onEscape: handleContentClose,
    "aria-label": "Informational Dialog"
  }, /*#__PURE__*/React.createElement(Grid.Container, {
    spacing: "none",
    direction: "column-reverse"
  }, /*#__PURE__*/React.createElement(Grid.Item, {
    xs: 12
  }, /*#__PURE__*/React.createElement("div", {
    className: "hc-pa-normal"
  }, /*#__PURE__*/React.createElement(Caption, {
    "aria-live": "polite",
    above: contentData.length !== 1 ? "".concat(currentSlideIndex + 1, " of ").concat(contentData.length) : null
  }, /*#__PURE__*/React.createElement(Heading, {
    size: 4,
    tabIndex: "0",
    ref: spotlightHeading
  }, contentData[currentSlideIndex].title)), /*#__PURE__*/React.createElement("p", {
    className: "hc-mt-normal"
  }, contentData[currentSlideIndex].copy))), contentData[currentSlideIndex].image && /*#__PURE__*/React.createElement(Grid.Item, {
    xs: 12
  }, /*#__PURE__*/React.createElement(Image, {
    width: "100%",
    height: "175px",
    src: "".concat(contentData[currentSlideIndex].image.src),
    alt: "".concat(contentData[currentSlideIndex].image.alt)
  }))), /*#__PURE__*/React.createElement("div", {
    className: "hc-pa-normal"
  }, /*#__PURE__*/React.createElement(Grid.Container, {
    spacing: "dense",
    direction: "row-reverse"
  }, contentData.length === 1 || currentSlideIndex === contentData.length - 1 ? /*#__PURE__*/React.createElement(Grid.Item, null, /*#__PURE__*/React.createElement(Button, {
    type: "primary",
    onClick: handleContentClose
  }, "Got It")) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Grid.Item, null, /*#__PURE__*/React.createElement(Button, {
    type: "primary",
    onClick: function onClick() {
      handleNextClick();
    }
  }, "Next")), /*#__PURE__*/React.createElement(Grid.Item, null, /*#__PURE__*/React.createElement(Button, {
    onClick: handleContentClose
  }, "End Tour")))))))), /*#__PURE__*/React.createElement(Overlay, {
    "data-testid": dataTestId ? "".concat(dataTestId, "_overlay") : '',
    type: hasOverlay ? 'subtle' : 'transparent',
    isVisible: true,
    onClick: handleContentClose
  }))), children);
};
Spotlight.displayName = 'Spotlight';

export { Spotlight as default };
